import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, List, Typography, Tag, Table, Spin, Alert } from 'antd';
import {
    SearchOutlined, DashboardOutlined, AppstoreOutlined, ShopOutlined,
    ShoppingCartOutlined, RocketOutlined, FileTextOutlined, SettingOutlined,
    TeamOutlined, BarcodeOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';

const { Text } = Typography;

interface CommandItem {
    key: string;
    label: string;
    path: string;
    icon: React.ReactNode;
    category: string;
    keywords: string[];
}

interface NlQueryResult {
    originalQuery: string;
    generatedSql: string;
    explanation: string;
    rows: Record<string, unknown>[];
    rowCount: number;
}

export const CommandPalette: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [nlResult, setNlResult] = useState<NlQueryResult | null>(null);
    const [nlLoading, setNlLoading] = useState(false);
    const [nlError, setNlError] = useState<string | null>(null);

    const commands: CommandItem[] = useMemo(() => [
        { key: 'dashboard', label: t('menu.dashboard'), path: '/', icon: <DashboardOutlined />, category: 'nav', keywords: ['home', '首页', 'dashboard'] },
        { key: 'products', label: t('menu.products'), path: '/products', icon: <AppstoreOutlined />, category: 'nav', keywords: ['product', 'sku', '商品', '产品'] },
        { key: 'inventory', label: t('menu.inventory'), path: '/inventory', icon: <ShopOutlined />, category: 'nav', keywords: ['stock', '库存', 'inventory'] },
        { key: 'scan', label: t('menu.stocktaking'), path: '/inventory/operations', icon: <BarcodeOutlined />, category: 'nav', keywords: ['scan', '扫码', '出入库'] },
        { key: 'orders', label: t('menu.orders'), path: '/orders', icon: <ShoppingCartOutlined />, category: 'nav', keywords: ['order', '订单'] },
        { key: 'quick-order', label: t('menu.quick_order'), path: '/orders/quick', icon: <ShoppingCartOutlined />, category: 'nav', keywords: ['quick', '快速', '下单'] },
        { key: 'po-list', label: t('menu.purchase_orders'), path: '/procurement/purchase-orders', icon: <FileTextOutlined />, category: 'procurement', keywords: ['po', '采购', 'purchase'] },
        { key: 'po-new', label: t('menu.purchase_order_create'), path: '/procurement/purchase-orders/new', icon: <FileTextOutlined />, category: 'procurement', keywords: ['new po', '新建采购'] },
        { key: 'pr-list', label: t('menu.purchase_requests'), path: '/purchase-requests', icon: <FileTextOutlined />, category: 'procurement', keywords: ['request', '请购'] },
        { key: 'flight-batches', label: t('menu.flight_batches'), path: '/flight-batches', icon: <RocketOutlined />, category: 'logistics', keywords: ['flight', '航班', '人肉'] },
        { key: 'pick-lists', label: t('menu.pick_lists'), path: '/pick-lists', icon: <FileTextOutlined />, category: 'logistics', keywords: ['pick', '拣货'] },
        { key: 'outbound', label: t('menu.outbound_records'), path: '/inventory/outbound-records', icon: <FileTextOutlined />, category: 'nav', keywords: ['outbound', '出库'] },
        { key: 'inbound', label: t('menu.inbound_records'), path: '/inventory/inbound-records', icon: <FileTextOutlined />, category: 'nav', keywords: ['inbound', '入库'] },
        { key: 'users', label: t('menu.users'), path: '/admin/users', icon: <TeamOutlined />, category: 'admin', keywords: ['user', '用户'] },
        { key: 'channels', label: t('menu.channels'), path: '/channels', icon: <SettingOutlined />, category: 'admin', keywords: ['channel', '渠道'] },
        { key: 'audit', label: t('menu.audit_logs'), path: '/admin/audit-logs', icon: <FileTextOutlined />, category: 'admin', keywords: ['audit', '审计', 'log'] },
    ], [t]);

    const filtered = useMemo(() => {
        if (!search.trim()) return commands;
        const kw = search.toLowerCase();
        return commands.filter(c =>
            c.label.toLowerCase().includes(kw) ||
            c.keywords.some(k => k.includes(kw))
        );
    }, [commands, search]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(prev => !prev);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const handleSelect = (path: string) => {
        setOpen(false);
        setSearch('');
        setNlResult(null);
        navigate(path);
    };

    const isNlQuery = search.startsWith('?') || (search.length > 10 && filtered.length === 0);

    const handleNlQuery = async () => {
        const query = search.startsWith('?') ? search.slice(1).trim() : search.trim();
        if (!query) return;
        setNlLoading(true);
        setNlError(null);
        setNlResult(null);
        try {
            const response = await apiClient.post('/nl-query', { query });
            setNlResult(response.data?.data);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Query failed';
            setNlError(msg);
        } finally {
            setNlLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isNlQuery && !nlLoading) {
            e.preventDefault();
            handleNlQuery();
        }
    };

    const nlColumns = nlResult?.rows?.[0]
        ? Object.keys(nlResult.rows[0]).map(key => ({
            title: key,
            dataIndex: key,
            key,
            ellipsis: true,
            render: (val: unknown) => val != null ? String(val) : '-',
        }))
        : [];

    return (
        <Modal
            open={open}
            onCancel={() => { setOpen(false); setSearch(''); setNlResult(null); setNlError(null); }}
            footer={null}
            closable={false}
            width={nlResult ? 900 : 500}
            styles={{ body: { padding: 0 } }}
        >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <Input
                    prefix={isNlQuery ? <ThunderboltOutlined style={{ color: '#722ed1' }} /> : <SearchOutlined />}
                    placeholder={t('command.placeholder')}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setNlResult(null); setNlError(null); }}
                    onKeyDown={handleKeyDown}
                    variant="borderless"
                    size="large"
                    autoFocus
                    suffix={
                        isNlQuery
                            ? <Tag color="purple" style={{ fontSize: 11 }}>{t('command.ai_query')}</Tag>
                            : <Tag style={{ fontSize: 11 }}>ESC</Tag>
                    }
                />
            </div>

            {nlLoading && (
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin tip={t('command.ai_thinking')} />
                </div>
            )}

            {nlError && (
                <div style={{ padding: 12 }}>
                    <Alert type="error" message={nlError} showIcon />
                </div>
            )}

            {nlResult && (
                <div style={{ padding: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        {nlResult.explanation} ({nlResult.rowCount} {t('command.results')})
                    </Text>
                    <Table
                        dataSource={nlResult.rows}
                        columns={nlColumns}
                        size="small"
                        rowKey={(_, i) => String(i)}
                        pagination={nlResult.rowCount > 10 ? { pageSize: 10, size: 'small' } : false}
                        scroll={{ x: 'max-content', y: 300 }}
                    />
                </div>
            )}

            {!nlLoading && !nlResult && !nlError && (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {isNlQuery ? (
                        <div style={{ padding: '16px', textAlign: 'center' }}>
                            <Text type="secondary">{t('command.press_enter_ai')}</Text>
                        </div>
                    ) : (
                        <List
                            dataSource={filtered}
                            renderItem={(item) => (
                                <List.Item
                                    onClick={() => handleSelect(item.path)}
                                    style={{ cursor: 'pointer', padding: '10px 16px' }}
                                    className="hover:bg-gray-50"
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontSize: 16, color: '#8c8c8c' }}>{item.icon}</span>
                                        <Text>{item.label}</Text>
                                    </div>
                                </List.Item>
                            )}
                            locale={{ emptyText: t('common.no_data') }}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
};
